// «Apartados ahora» de la ficha del SKU: liga cada apartado vivo del catálogo con su venta del wizard.
import type { InventoryLiveReservation } from '@/api/services/retail-catalog/backoffice';
import type { WizardSessionSummary } from '@/api/services/retail-wizard/backoffice';
import { formatTimeMx } from '@/lib/dateHelpers';
import type { PillTone } from '@/shared/patterns/Pills';
import { storeInfo, type StoreDirectory } from './inventoryDetail';

/** En qué va la venta que retiene el apartado; `UNKNOWN` si el wizard no respondió por ella. */
export type LiveReservationState =
    | 'IN_SALE'
    | 'EXPIRED'
    | 'PAID_PENDING_DELIVERY'
    | 'RELEASING'
    | 'UNKNOWN';

export interface LiveReservationRow {
    reservationId: string;
    storeId: string;
    storeName: string;
    storeCode: string;
    quantity: number;
    sellerName: string;
    reservedAt: string | null;
    expiresAt: string;
    state: LiveReservationState;
    /** Venta cobrada a la que lleva «Ver venta»; `null` mientras no hay venta. */
    saleId: string | null;
}

const CLOSED_STATES: ReadonlySet<string> = new Set(['CANCELLED', 'EXPIRED']);

/**
 * Estado del apartado según su sesión: cobrada pesa más que cerrada, porque falta entregar.
 * Sin cobro y con `expiresAt` pasado, el apartado sigue contando hasta que el barrido lo suelte.
 */
export function reservationState(
    session: WizardSessionSummary | undefined,
    expiresAt: string,
    now: number,
): LiveReservationState {
    if (session && (session.paid || session.state === 'COMPLETED')) return 'PAID_PENDING_DELIVERY';
    if (session && CLOSED_STATES.has(session.state)) return 'RELEASING';
    if (Date.parse(expiresAt) <= now) return 'EXPIRED';
    return session ? 'IN_SALE' : 'UNKNOWN';
}

/** Sesiones a resumir, sin repetir y en orden estable para la llave de la consulta. */
export function reservationSessionIds(reservations: readonly InventoryLiveReservation[]): string[] {
    return [...new Set(reservations.map((r) => r.sessionId))].sort();
}

/** Renglones de la tarjeta, de la tienda elegida si la hay, alfabéticos por tienda y luego por hora. */
export function liveReservationRows(
    reservations: readonly InventoryLiveReservation[],
    sessions: readonly WizardSessionSummary[],
    stores: StoreDirectory,
    storeId: string | undefined,
    now: number = Date.now(),
): LiveReservationRow[] {
    const sessionOf = new Map(sessions.map((s) => [s.sessionId, s]));
    return reservations
        .filter((r) => !storeId || r.storeId === storeId)
        .map((r) => {
            const session = sessionOf.get(r.sessionId);
            const store = storeInfo(stores, r.storeId);
            return {
                reservationId: r.reservationId,
                storeId: r.storeId,
                storeName: store.name,
                storeCode: store.code,
                quantity: r.quantity,
                sellerName: session?.sellerName ?? '—',
                reservedAt: r.reservedAt,
                expiresAt: r.expiresAt,
                state: reservationState(session, r.expiresAt, now),
                saleId: session?.saleId ?? null,
            };
        })
        .sort(
            (a, b) =>
                a.storeName.localeCompare(b.storeName, 'es') ||
                (a.reservedAt ?? '').localeCompare(b.reservedAt ?? ''),
        );
}

export interface ReservationStateView {
    label: string;
    tone: PillTone;
    /** El renglón que pide acción: cobrada y sin entregar. */
    highlight: boolean;
}

/** Etiqueta, tono y resaltado del estado del apartado. */
export function reservationStateView(
    row: Pick<LiveReservationRow, 'state' | 'expiresAt'>,
): ReservationStateView {
    const expires = formatTimeMx(row.expiresAt);
    switch (row.state) {
        case 'IN_SALE':
            return { label: `En venta · vence ${expires}`, tone: 'warning', highlight: false };
        case 'EXPIRED':
            return {
                label: 'Vencido · se libera en la próxima pasada',
                tone: 'neutral',
                highlight: false,
            };
        case 'PAID_PENDING_DELIVERY':
            return { label: 'Cobrada · falta entregar', tone: 'warning', highlight: true };
        case 'RELEASING':
            return { label: 'Liberando', tone: 'neutral', highlight: false };
        case 'UNKNOWN':
            return { label: `Apartada · vence ${expires}`, tone: 'neutral', highlight: false };
    }
}

/** Hora del apartado, o guion si es anterior al campo. */
export function reservedSinceLabel(reservedAt: string | null): string {
    return reservedAt ? formatTimeMx(reservedAt) : '—';
}
